import * as z from "zod";
import { code, cursorPagination, timeOfMinutes, uuidV7, version } from "../index";
import { type BookingStatus, bookingStatuses, meetingTypes, offeredBrandInterests } from "./enums";
import { timeOfDay } from "./fields";

// Appointments (50-api/booking.md §2.3): what a customer books for a sales campaign. One time and one showroom, and
// one row of `booking.booking` per brand met (ADR-089).

/** The slots of a showroom day, as in the current portal: one every 30 minutes, the last one starting at 18:00. */
export const SLOT_MINUTES = 30;
export const FIRST_SLOT = "09:00";
export const LAST_SLOT = "18:00";

export const MAX_BRANDS_PER_APPOINTMENT = 30;

/** The most contacts of the customer an appointment booked by Tomorrow is confirmed to (B-170, ADR-312). */
export const MAX_APPOINTMENT_RECIPIENTS = 10;

const minutesOf = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

/** Every slot of a showroom day, in order. */
export function slotTimes(): string[] {
  const times: string[] = [];
  for (let minutes = minutesOf(FIRST_SLOT); minutes <= minutesOf(LAST_SLOT); minutes += SLOT_MINUTES) {
    times.push(timeOfMinutes(minutes));
  }
  return times;
}

const closed = { cancelled: [], completed: [], no_show: [] } as const;

/**
 * What can still happen to an appointment, one table per action: a moved one can be moved again, a cancelled one
 * stays so. The worker builds its state machines from these, the apps offer the actions with them.
 */
export const appointmentTransitions = {
  reschedule: { scheduled: ["rescheduled"], rescheduled: ["rescheduled"], ...closed },
  cancel: { scheduled: ["cancelled"], rescheduled: ["cancelled"], ...closed },
} as const satisfies Record<string, Record<BookingStatus, readonly BookingStatus[]>>;
export type AppointmentAction = keyof typeof appointmentTransitions;

/** Whether an appointment in a status can still be moved or cancelled. */
export const canMoveAppointment = (action: AppointmentAction, status: BookingStatus): boolean =>
  appointmentTransitions[action][status].length > 0;

/** The start of a slot, in the time zone of the showroom. */
const slotTime = timeOfDay.refine((time) => slotTimes().includes(time), "Not the start of a slot");

const brandOrgIds = z
  .array(z.uuid())
  .min(1)
  .max(MAX_BRANDS_PER_APPOINTMENT)
  .refine((ids) => new Set(ids).size === ids.length, "A brand is named once");

/** Where and when the customer comes, and how it meets the brands. */
const slot = z.object({
  showroomLocation: code,
  /** The day and the time of the showroom, whatever the time zone of who books. */
  date: z.iso.date(),
  time: slotTime,
  meetingType: z.enum(meetingTypes),
});

const appointmentFilters = {
  salesCampaignId: z.uuid().optional(),
  brandOrgId: z.uuid().optional(),
  customerOrgId: z.uuid().optional(),
  status: z.enum(bookingStatuses).optional(),
  /** Showroom days, both included: an appointment is of the day its showroom has when it starts. */
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
};

/** The appointments visible to the Actor, the earliest first; without `status`, the ones not cancelled. */
export const listAppointmentsInput = z.object({
  ...appointmentFilters,
  ...cursorPagination({ defaultLimit: 50, maxLimit: 200 }),
});
export type ListAppointmentsInput = z.input<typeof listAppointmentsInput>;

/** How many appointments the list has with the same filters, each counted once whatever its brands (ADR-086). */
export const countAppointmentsInput = z.object(appointmentFilters);
export type CountAppointmentsInput = z.input<typeof countAppointmentsInput>;

export const getAppointmentInput = z.object({ appointmentId: z.uuid() });
export type GetAppointmentInput = z.input<typeof getAppointmentInput>;

/**
 * Books a customer with one or more brands at the same time. The id comes from the client (ADR-046): a retry by the
 * same user returns the appointment already booked. Recipients, buying group and `notifyCustomer` are Tomorrow's
 * alone (the internal version of the spec of the Sales team, B-170, ADR-312): a customer's link books for its contact.
 */
export const bookAppointmentInput = slot
  .extend({
    id: uuidV7,
    salesCampaignId: z.uuid(),
    customerOrgId: z.uuid(),
    contactPersonId: z.uuid().nullable().optional(),
    brandOrgIds,
    /** Names of the categories the customer buys. */
    categories: z.array(code).max(20).optional(),
    notes: z.string().trim().min(1).max(2000).nullable().optional(),
    /** The buying group (the store) of the contact the appointment is for, when the contact has more than one. */
    buyingGroupId: z.uuid().nullable().optional(),
    /** The contacts of the customer the confirmation goes to, the contact of the appointment among them. */
    recipientContactIds: z
      .array(z.uuid())
      .min(1)
      .max(MAX_APPOINTMENT_RECIPIENTS)
      .refine((ids) => new Set(ids).size === ids.length, "A contact is named once")
      .optional(),
    /** False books it without emailing the customer, neither its confirmation nor its reminder; Tomorrow's people hear of it. */
    notifyCustomer: z.boolean().default(true),
  })
  .refine(
    (input) =>
      input.recipientContactIds === undefined ||
      (input.contactPersonId != null && input.recipientContactIds.includes(input.contactPersonId)),
    { path: ["recipientContactIds"], message: "The contact of the appointment is one of its recipients" },
  );
export type BookAppointmentInput = z.input<typeof bookAppointmentInput>;

/** Moves an appointment, with every brand it has, to another slot, showroom or type of meeting. */
export const rescheduleAppointmentInput = slot.extend({ appointmentId: z.uuid(), version });
export type RescheduleAppointmentInput = z.input<typeof rescheduleAppointmentInput>;
/** The gateway routes split the input: the id in the path, the rest in the body. */
export const appointmentParams = rescheduleAppointmentInput.pick({ appointmentId: true });
export const rescheduleAppointmentBody = rescheduleAppointmentInput.omit({ appointmentId: true });

/** The reason goes to the audit. */
export const cancelAppointmentInput = z.object({
  appointmentId: z.uuid(),
  version,
  reason: z.string().trim().min(1).max(500).optional(),
});
export type CancelAppointmentInput = z.input<typeof cancelAppointmentInput>;
export const cancelAppointmentBody = cancelAppointmentInput.omit({ appointmentId: true });

/** The slots of a showroom day in which every brand named can still take the customer. */
export const findAppointmentSlotsInput = z.object({
  salesCampaignId: z.uuid(),
  showroomLocation: code,
  date: z.iso.date(),
  brandOrgIds,
  /** Only the slots of the brands that take this type of meeting. */
  meetingType: z.enum(meetingTypes).optional(),
  /** The appointment being moved: its own bookings do not take a place. */
  appointmentId: z.uuid().optional(),
});
export type FindAppointmentSlotsInput = z.input<typeof findAppointmentSlotsInput>;

/** The customers whose name, store or code contains `q`, with their contacts: who an appointment is booked for. */
export const listAppointmentCustomersInput = z.object({
  q: z.string().trim().min(2).max(100),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ListAppointmentCustomersInput = z.input<typeof listAppointmentCustomersInput>;

export const appointmentDto = z
  .object({
    id: z.uuid(),
    salesCampaign: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    customer: z.object({ orgId: z.uuid(), name: z.string() }).nullable(),
    /** Who comes: a field rule can hide it. */
    contact: z
      .object({ id: z.uuid(), name: z.string().nullable(), email: z.string().nullable() })
      .nullable()
      .optional(),
    showroomLocation: z.string().nullable(),
    /** IANA time zone of the showroom: `date` and `time` are in it. `UTC` for an appointment without a showroom. */
    timezone: z.string(),
    date: z.iso.date(),
    time: timeOfDay,
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    meetingType: z.string().nullable(),
    notes: z.string().nullable().optional(),
    status: z.enum(bookingStatuses),
    /** How many times it was moved or cancelled: the sequence of its calendar event. */
    sequence: z.number().int(),
    /** First and last name of the internal user who booked it; null when the customer did. */
    bookedBy: z.string().nullable(),
    /** The customer booked it itself, from its personal link or in Frontrow (ADR-237). */
    bookedByCustomer: z.boolean(),
    /** The brands the Actor sees of the appointment, by name. */
    brands: z.array(z.object({ bookingId: z.uuid(), brandOrgId: z.uuid(), name: z.string() })),
    categories: z.array(z.string()),
    /** The buying group (the store) Tomorrow booked it for (ADR-312). */
    buyingGroup: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
    /** False when Tomorrow booked it without emailing the customer (ADR-312). */
    notifyCustomer: z.boolean().optional(),
    /**
     * The contacts of the customer its confirmation went to, when Tomorrow named more than its contact (ADR-312); only
     * in the appointment read alone. A field rule can hide it, as `contact`.
     */
    recipients: z
      .array(z.object({ id: z.uuid(), name: z.string().nullable(), email: z.string().nullable() }))
      .optional(),
    version: z.number().int(),
  })
  .meta({ id: "Appointment" });

export const appointmentSlotsDto = z
  .object({
    date: z.iso.date(),
    timezone: z.string(),
    /** Every slot of the day; `free` is how many more customers the fullest brand takes, 0 when one cannot. */
    slots: z.array(z.object({ time: timeOfDay, startsAt: z.iso.datetime(), free: z.number().int() })),
    /** The types of meeting every brand takes that day. */
    meetingTypes: z.array(z.enum(meetingTypes)),
    /** The brands without an availability in this showroom that day: with one of them no slot is free. */
    unavailableBrandOrgIds: z.array(z.uuid()),
  })
  .meta({ id: "AppointmentSlots" });

export const appointmentCustomerDto = z
  .object({
    orgId: z.uuid(),
    name: z.string(),
    customerCode: z.string().nullable(),
    storeName: z.string().nullable(),
    contacts: z.array(
      z.object({
        id: z.uuid(),
        name: z.string().nullable(),
        email: z.string().nullable(),
        /** The buying groups (the stores) of the contact not archived, by name (ADR-110, ADR-312). */
        buyingGroups: z.array(z.object({ id: z.uuid(), name: z.string() })).optional(),
        /** What the contact is to each brand it buys or Tomorrow wants it to: the form chooses and marks them. */
        brandInterests: z.array(z.object({ brandOrgId: z.uuid(), interest: z.enum(offeredBrandInterests) })).optional(),
      }),
    ),
  })
  .meta({ id: "AppointmentCustomer" });
export const appointmentCustomersDto = z.object({ items: z.array(appointmentCustomerDto) });
